import STEP_DEFINITIONS from '../../../lib/stepDefinitions';

/**
 * Attendance status options for live training attestation
 */
export const ATTENDANCE_OPTIONS = [
  { value: 'on_time', label: 'On-time & attended' },
  { value: 'late_or_left_early', label: 'Attended but late / left early' },
  { value: 'did_not_attend', label: 'Did not attend' }
];

// Step codes pulled from the canonical STEP_DEFINITIONS so renames there
// automatically propagate here. Meetings live at the STEP level in the
// backend (`user_training.wizard_step_definition_id` → WizardStepDefinition
// with a STEP code) — see seed_default_trainings.js — so step codes are the
// right key for both UI config lookup and meeting matching.
const KICKOFF_CODE = STEP_DEFINITIONS.KICKOFF_LIVE_TRAINING.code;
const FRONT_OFFICE_CODE = STEP_DEFINITIONS.FRONT_OFFICE_LIVE_TRAINING.code;
const CLINICAL_CODE = STEP_DEFINITIONS.CLINICAL_LIVE_TRAINING.code;
const BACK_OFFICE_CODE = STEP_DEFINITIONS.BACK_OFFICE_LIVE_TRAINING.code;
const HARD_LAUNCH_CODE = STEP_DEFINITIONS.HARD_LAUNCH_LIVE_TRAINING.code;

// Meeting names that the backend seeds onto user_training rows (see
// backend/utility/wizard/seed_default_trainings.js). Pinned here because
// backend uses "live working session" while the frontend sidebar shows
// "live training" — two different display strings, but the seed wins for
// the join. findMeetingForStep prefers an ID-based join when the wizard
// state exposes step_definition_id, falling back to this name.
const MEETING_NAMES = {
  [KICKOFF_CODE]: 'Kickoff live working session',
  [FRONT_OFFICE_CODE]: 'Front office live working session',
  [CLINICAL_CODE]: 'Clinical live working session',
  [BACK_OFFICE_CODE]: 'Back office live working session',
  [HARD_LAUNCH_CODE]: 'Hard launch live working session'
};

/**
 * Configuration for each live training step, keyed by STEP code.
 */
export const LIVE_TRAINING_CONFIG = {
  [KICKOFF_CODE]: {
    title: STEP_DEFINITIONS.KICKOFF_LIVE_TRAINING.name,
    description:
      'Review the requirements and set due dates based on your confirmed live working session schedule. To request a change to a confirmed live working session date, contact your dedicated onboarding team.',
    meetingName: MEETING_NAMES[KICKOFF_CODE],
    responsibilities: [
      'Confirm setup',
      'Create test patient (used during training by all staff members)',
      'Staff permissions',
      'Staff assignments',
      'Data ready / requested (if applicable)'
    ],
    attestationConfirmations: [
      'Backend and administrative setup in Myriad Motion has been completed, and required credentials have been added and validated.',
      'Kickoff and basic system training has been completed with the client.'
    ],
    confirmationKeys: ['backendSetupCompleted', 'kickoffTrainingCompleted']
  },
  [FRONT_OFFICE_CODE]: {
    title: STEP_DEFINITIONS.FRONT_OFFICE_LIVE_TRAINING.name,
    description:
      'Review the requirements and set due dates based on your confirmed live working session schedule. To request a change to a confirmed live working session date, contact your dedicated onboarding team.',
    meetingName: MEETING_NAMES[FRONT_OFFICE_CODE],
    responsibilities: [
      'Healthcare program education',
      'Healthcare program setup (GFE + Pre-authorized healthcare)',
      'Test card reader + virtual terminal',
      'Upload schedule of fees',
      'Schedule test appointment',
      'Set up self-scheduling (if applicable)'
    ],
    attestationConfirmations: [
      'Front office workflows in Myriad Health have been reviewed, and front office training has been completed with the client.',
      'The client has been introduced to patient scheduling, check-in, and payment processing functionality.'
    ],
    confirmationKeys: ['frontOfficeWorkflowsReviewed', 'frontOfficeTrainingCompleted']
  },
  [CLINICAL_CODE]: {
    title: STEP_DEFINITIONS.CLINICAL_LIVE_TRAINING.name,
    description:
      'Review the requirements and set due dates based on your confirmed live working session schedule. To request a change to a confirmed live working session date, contact your dedicated onboarding team.',
    meetingName: MEETING_NAMES[CLINICAL_CODE],
    responsibilities: [
      'Forms & templates built (verified by principal user)',
      'Codes / products added (if 3D charting enabled; enables checkout flow)',
      'Watch product tour / video'
    ],
    attestationConfirmations: [
      'Clinical workflows in Myriad Health have been reviewed, and clinical training has been completed with the client.',
      'The client has been introduced to available charting methods and clinical form functionality.'
    ],
    confirmationKeys: ['clinicalWorkflowsReviewed', 'chartingMethodsIntroduced']
  },
  [BACK_OFFICE_CODE]: {
    title: 'Back office billing live working session',
    description:
      'Review the requirements and set due dates based on your confirmed live working session schedule. To request a change to a confirmed live working session date, contact your dedicated onboarding team.',
    meetingName: MEETING_NAMES[BACK_OFFICE_CODE],
    responsibilities: [
      'EDI enrollments submitted',
      'Verify schedule of fees & allowables',
      'Full data import verified (principal user)',
      'File a test claim / send invoice',
      'Run a patient payment (Card-on-file) (Pre-authorized healthcare form)',
      'Watch product tour / video'
    ],
    attestationConfirmations: [
      'Back office billing workflows in Myriad Health have been reviewed, and billing training has been completed with the client.',
      'The client has been introduced to claims processing, ERA posting, and payment reconciliation functionality.'
    ],
    confirmationKeys: ['billingWorkflowsReviewed', 'backOfficeTrainingCompleted']
  },
  [HARD_LAUNCH_CODE]: {
    title: STEP_DEFINITIONS.HARD_LAUNCH_LIVE_TRAINING.name,
    description:
      'Review the requirements and set due dates based on your confirmed live working session schedule. To request a change to a confirmed live working session date, contact your dedicated onboarding team.',
    meetingName: MEETING_NAMES[HARD_LAUNCH_CODE],
    responsibilities: [
      'Live appointments scheduled and/or live patient check-out',
      'Live patient check in and check out completed',
      'Full system use'
    ],
    attestationConfirmations: [
      'All onboarding phases have been completed, and the practice is fully prepared for live operations.',
      'The client has demonstrated proficiency in all trained workflows and is ready for hard launch.'
    ],
    confirmationKeys: ['firmGoLiveOccurred', 'hardLaunchSessionCompleted']
  }
};

/**
 * Get the configuration for a specific training step
 * @param {string} stepCode - The STEP code (parent of the LiveTraining substep)
 * @returns {Object|null} The training configuration or null if not found
 */
export const getTrainingConfig = (stepCode) => {
  return LIVE_TRAINING_CONFIG[stepCode] || null;
};

/**
 * Either step argument may be omitted — the function returns whatever it
 * finds with the data it has.
 *
 * @param {Array} meetings - From useGetUserTrainings (`data.meetings`)
 * @param {string} stepCode - The wizard STEP code (e.g. 'onboarding__kickoff_live_training')
 * @param {Array} [wizardSteps] - Optional. `wizardState.steps` from StepperContext.
 * @returns {Object|null} The matched meeting, or null
 */
export const findMeetingForStep = (meetings, stepCode, wizardSteps) => {
  if (!Array.isArray(meetings) || !stepCode) return null;

  if (Array.isArray(wizardSteps)) {
    const step = wizardSteps.find((s) => s.code === stepCode);
    const stepDefId = step?.step_definition_id;
    if (stepDefId) {
      const byId = meetings.find((m) => m.wizard_step_definition_id === stepDefId);
      if (byId) return byId;
    }
  }

  const expectedName = MEETING_NAMES[stepCode]?.toLowerCase();
  if (!expectedName) return null;
  return meetings.find((m) => m.name?.toLowerCase() === expectedName) || null;
};
